import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { Close, BlockRounded, CheckRounded } from "@mui/icons-material";

const PRESET_KEYS = [
  "slipUnclear",
  "amountMismatch",
  "transferNotFound",
  "suspiciousSlip",
  "duplicateSlip",
  "wrongCustomer",
];

const ERROR_FG = "#c0343a";
const ERROR_BG = "#fdeef0";
const BORDER = "#ece9f1";
const TEXT_SUBTLE = "#a8a3b4";

// Bento-style reason picker for rejecting a shipping-payment approval. Presets fill
// the editable field; the typed text is what actually gets submitted.
export default function RejectReasonDialog({ open, onClose, onConfirm, busy = false }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const presets = PRESET_KEYS.map((k) => ({ key: k, text: t(`financial.rejectReasons.${k}`) }));
  const trimmed = reason.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        {/* header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: ERROR_BG, color: ERROR_FG, flexShrink: 0 }}>
              <BlockRounded />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.15 }}>{t("financial.rejectReason")}</Typography>
              <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{t("financial.rejectSubtitle")}</Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Box>

        {/* preset chips (Bento tiles) */}
        <Typography sx={{ mt: 2.25, mb: 1, fontSize: 10.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: TEXT_SUBTLE }}>
          {t("financial.rejectQuick")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
          {presets.map((p) => {
            const active = trimmed === p.text;
            return (
              <Box
                key={p.key}
                onClick={() => setReason(p.text)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "12px",
                  cursor: "pointer",
                  userSelect: "none",
                  border: `1.5px solid ${active ? ERROR_FG : BORDER}`,
                  background: active ? ERROR_BG : "#faf9fc",
                  transition: "border-color .12s, background .12s",
                  "&:hover": { borderColor: active ? ERROR_FG : "#d9cffb" },
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: active ? ERROR_FG : "#2c2937" }}>
                  {p.text}
                </Typography>
                {active && <CheckRounded sx={{ fontSize: 17, color: ERROR_FG, flexShrink: 0 }} />}
              </Box>
            );
          })}
        </Box>

        {/* editable reason */}
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("financial.reasonPlaceholder")}
          color="secondary"
          sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.25, sm: 3 }, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={busy} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="error"
          startIcon={<BlockRounded />}
          disabled={busy || !trimmed}
          onClick={() => onConfirm(trimmed)}
          sx={{ borderRadius: "12px", fontWeight: 700, px: 3 }}
        >
          {t("financial.confirmReject")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
